import type {
  LocalProjectionPayload,
  RuntimeCursor,
  StockMovementHistoryRuntimeItem,
} from "../gateway/local-projection-client";
import { indexedScope } from "./indexed-runtime";
import { posRuntimeDb } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

function dataScope(identity: TrustedTerminalIdentity) {
  return indexedScope(identity, "stock-history");
}

function movementKey(scopeKey: string, movementId: string) {
  return `${scopeKey}:movement:${movementId}`;
}

function cursorKey(identity: TrustedTerminalIdentity) {
  return `local-index:cursor:${indexedScope(identity, "stock_history")}`;
}

async function writeCursor(
  identity: TrustedTerminalIdentity,
  cursor: RuntimeCursor | null,
  fallbackServerAt: string,
) {
  const effective = cursor?.changed_at && cursor.entity_id
    ? cursor
    : { changed_at: fallbackServerAt, entity_id: ZERO_UUID };
  const now = new Date().toISOString();
  await posRuntimeDb.runtimeMeta.put({
    key: cursorKey(identity),
    scopeKey: indexedScope(identity, "stock_history"),
    value: effective,
    updatedAt: now,
  });
}

export async function applyStockMovementRuntime(
  identity: TrustedTerminalIdentity,
  payload: LocalProjectionPayload<"STOCK_HISTORY">,
) {
  const scopeKey = dataScope(identity);
  await posRuntimeDb.transaction("rw", posRuntimeDb.stockMovementRuntime, posRuntimeDb.runtimeMeta, async () => {
    if (payload.mode === "FULL" || payload.mode === "DISABLED") {
      await posRuntimeDb.stockMovementRuntime.where("scopeKey").equals(scopeKey).delete();
    }
    if (payload.mode !== "DISABLED") {
      for (const item of payload.items) {
        await posRuntimeDb.stockMovementRuntime.put({
          key: movementKey(scopeKey, item.movement_id),
          scopeKey,
          movementId: item.movement_id,
          stockItemId: item.stock_item_id,
          occurredAt: item.occurred_at,
          changedAt: item.changed_at,
          payload: item,
        });
      }
    }
    await writeCursor(identity, payload.next_cursor, payload.server_at);
  });
}

export async function readLocalStockMovementHistory(
  identity: TrustedTerminalIdentity,
  stockItemId: string,
): Promise<StockMovementHistoryRuntimeItem[]> {
  const scopeKey = dataScope(identity);
  const rows = await posRuntimeDb.stockMovementRuntime
    .where("[scopeKey+stockItemId]")
    .equals([scopeKey, stockItemId])
    .toArray();

  return rows
    .flatMap((row) => row.payload && typeof row.payload === "object"
      ? [row.payload as StockMovementHistoryRuntimeItem]
      : [])
    .sort((left, right) => {
      const occurred = right.occurred_at.localeCompare(left.occurred_at);
      return occurred || right.movement_id.localeCompare(left.movement_id);
    });
}

export async function purgeStockMovementRuntimeOutsideScope(identity: TrustedTerminalIdentity) {
  const keepPrefix = `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}:`;
  await posRuntimeDb.stockMovementRuntime
    .filter((row) => !row.scopeKey.startsWith(keepPrefix))
    .delete();
}
